import SEO from "../components/SEO";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Sun, Moon, ArrowLeft, Car, MapPin, Circle, Zap, Clock, 
  ChevronRight, ChevronLeft, AlertCircle, CheckCircle, Info, Check,
  Search, Loader2
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

// Standalone services - can select multiple
const SERVICES = [
  { id: "tyres", label: "Tyres", description: "New tyres fitted and balanced" },
  { id: "alignment", label: "Wheel Alignment", description: "Wheel alignment service" },
  { id: "rotation", label: "Tyre Rotation", description: "Rotate tyres for even wear" }
];

const PRICE_RANGES = [
  { value: "lower", label: "Budget Friendly", description: "Economy options" },
  { value: "mid", label: "Mid Range", description: "Quality & value balance" },
  { value: "luxury", label: "Premium", description: "High-performance tyres" },
  { value: "winter", label: "Winter Tyres", description: "Cold weather performance" },
  { value: "all_season", label: "All Season", description: "Year-round versatility" },
  { value: "ev", label: "EV Tyres", description: "Electric vehicle optimised" }
];

// Comparison intent (Feb 2026) — see /api/customer/quote-request payload.
// "specific": customer knows the exact brand/model they want → ask for both.
// "budget":   customer wants the best within a budget tier + driving priority.
// "open":     customer is open to recommendations (default).
const COMPARISON_INTENTS = [
  {
    value: "specific",
    label: "I know the tyre brand/model I want",
    description: "Compare prices from multiple tyre shops for the same tyre.",
  },
  {
    value: "budget",
    label: "I'm looking for the best option within my budget",
    description: "Receive tyre recommendations based on your budget and driving needs.",
  },
  {
    value: "open",
    label: "I'm open to different options",
    description: "Receive a range of tyre options recommended by local tyre shops.",
  },
];

const DRIVING_PRIORITIES = [
  { value: "long_lasting", label: "Long lasting" },
  { value: "quiet_ride", label: "Quiet ride" },
  { value: "performance", label: "Performance" },
  { value: "ev_efficiency", label: "EV efficiency" },
  { value: "wet_grip", label: "Wet weather grip" },
];

const BUDGET_LEVELS = [
  { value: "lower", label: "Budget" },
  { value: "mid", label: "Mid-range" },
  { value: "luxury", label: "Premium" },
];

const TYRE_QUANTITIES = [
  { value: 1, label: "1 Tyre" },
  { value: 2, label: "2 Tyres" },
  { value: 4, label: "4 Tyres (Full Set)" },
  { value: 5, label: "5 Tyres (Inc. Spare)" }
];

export default function QuoteRequest() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [tyreErrors, setTyreErrors] = useState({ width: "", aspect: "", rim: "" });
  // Step 2 ("What are you looking for?") is only shown when tyres is selected.
  // For non-tyre flows the user jumps from step 1 (services) → step 3 (vehicle).
  const totalSteps = 5;

  // Form state - now with multi-select services
  const [formData, setFormData] = useState({
    selected_services: [], // Array of selected service IDs: ["tyres", "alignment", "rotation"]
    rego: "",
    tyre_specs: {
      width: "",
      aspect_ratio: "",
      rim_size: ""
    },
    brand_preference: "",
    price_range: "mid",
    // Feb 2026: comparison intent + branched fields
    comparison_intent: "",       // "" (unset) | "specific" | "budget" | "open"
    tyre_brand: "",              // mandatory when intent="specific"
    tyre_model: "",              // mandatory when intent="specific"
    driving_priority: "",        // mandatory when intent="budget"
    quantity: 4,
    address: user?.address || null,
    search_radius: 25,
    request_type: "standard",
    customer_note: "",
    // Feb 2026: CarJam ABCD lookup (populated when customer clicks "Look up
    // vehicle" on the rego field). The customer can review/edit any value.
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_sub_model: "",
    vehicle_body_type: "",
    vehicle_fuel_type: "",
    vehicle_lookup_source: "", // "carjam" | "manual" | ""
  });

  // CarJam lookup state (separate from form so we can show a spinner +
  // success/error message inline without rerendering the whole form).
  const [lookupState, setLookupState] = useState({
    status: "idle", // "idle" | "loading" | "found" | "not_found" | "pending" | "error" | "manual"
    message: "",
  });

  // Helper to check if tyres service is selected
  const hasTyresSelected = formData.selected_services.includes("tyres");
  
  // Helper to check if only non-tyre services are selected (alignment or rotation only)
  const hasOnlyNonTyreServices = formData.selected_services.length > 0 && 
    !formData.selected_services.includes("tyres");

  // Rego is required for any combo EXCEPT "tyres only"
  const isRegoRequired = formData.selected_services.length > 0 && 
    !(formData.selected_services.length === 1 && formData.selected_services.includes("tyres"));

  // Tyre specs are mandatory whenever tyres is selected
  const isTyreSpecsRequired = hasTyresSelected;

  // Rego validation
  const [regoError, setRegoError] = useState("");
  const validateRego = (value) => {
    if (!value) {
      setRegoError("");
      return true;
    }
    if (value.length > 6) {
      setRegoError("Maximum 6 characters");
      return false;
    }
    if (!/^[A-Za-z0-9]*$/.test(value)) {
      setRegoError("Letters and numbers only");
      return false;
    }
    setRegoError("");
    return true;
  };

  const toggleService = (serviceId) => {
    setFormData(prev => {
      const services = prev.selected_services.includes(serviceId)
        ? prev.selected_services.filter(s => s !== serviceId)
        : [...prev.selected_services, serviceId];
      return { ...prev, selected_services: services };
    });
  };

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateTyreSpecs = (key, value) => {
    setFormData(prev => ({
      ...prev,
      tyre_specs: { ...prev.tyre_specs, [key]: value }
    }));
  };

  // Convert selected services to service_type string for backend compatibility
  const getServiceType = () => {
    const services = formData.selected_services;
    const hasTyres = services.includes("tyres");
    const hasAlignment = services.includes("alignment");
    const hasRotation = services.includes("rotation");
    
    if (hasTyres && hasAlignment && hasRotation) return "tyres_alignment_rotation";
    if (hasTyres && hasAlignment) return "tyres_alignment";
    if (hasTyres && hasRotation) return "tyres_rotation";
    if (hasAlignment && hasRotation) return "alignment_rotation";
    if (hasTyres) return "tyres_only";
    if (hasAlignment) return "alignment_only";
    if (hasRotation) return "rotation_only";
    return "tyres_only"; // Default
  };

  const handleSubmit = async () => {
    if (formData.selected_services.length === 0) {
      toast.error("Please select at least one service");
      return;
    }

    if (!formData.address || !formData.address.full_address) {
      toast.error("Please enter your address");
      return;
    }

    // Rego is required when tyres are selected OR for non-tyres-only services
    if ((hasTyresSelected || isRegoRequired) && !formData.rego) {
      toast.error("Vehicle registration (rego) is required");
      return;
    }

    // Rego validation
    if (formData.rego && (formData.rego.length > 6 || !/^[A-Za-z0-9]*$/.test(formData.rego))) {
      toast.error("Registration must be max 6 alphanumeric characters");
      return;
    }

    // For tyre services: rego + all tyre specs are mandatory with range validation
    if (hasTyresSelected) {
      const missing = [];
      if (!formData.rego) missing.push("rego");
      if (!formData.tyre_specs.width) missing.push("tyre width");
      if (!formData.tyre_specs.aspect_ratio) missing.push("profile (aspect ratio)");
      if (!formData.tyre_specs.rim_size) missing.push("rim size");
      if (missing.length > 0) {
        toast.error(`Please fill in the following required fields: ${missing.join(", ")}`);
        return;
      }

      const width = parseInt(formData.tyre_specs.width);
      const aspect = parseInt(formData.tyre_specs.aspect_ratio);
      const rim = parseInt(formData.tyre_specs.rim_size);
      const errors = { width: "", aspect: "", rim: "" };
      let hasRangeError = false;
      if (width < 150 || width > 320) { errors.width = "Width is outside the accepted range"; hasRangeError = true; }
      if (aspect < 10 || aspect > 100) { errors.aspect = "Aspect ratio is outside the accepted range"; hasRangeError = true; }
      if (rim < 13 || rim > 25) { errors.rim = "Rim size is outside the accepted range"; hasRangeError = true; }
      if (hasRangeError) {
        setTyreErrors(errors);
        toast.error("Please correct the tyre specification errors");
        return;
      }
    }

    // Customer note is mandatory
    if (!formData.customer_note || !formData.customer_note.trim()) {
      toast.error("Please add a note for the tyre shops");
      return;
    }

    setLoading(true);

    // Build the vehicle_info for payload — include CarJam-verified data if
    // the customer ran the lookup.
    const vehicleInfo = {};
    if (formData.rego) vehicleInfo.rego = formData.rego.toUpperCase();
    if (formData.vehicle_make) vehicleInfo.make = formData.vehicle_make;
    if (formData.vehicle_model) vehicleInfo.model = formData.vehicle_model;
    if (formData.vehicle_year) {
      const y = parseInt(formData.vehicle_year, 10);
      if (!isNaN(y)) vehicleInfo.year = y;
    }
    if (formData.vehicle_sub_model) vehicleInfo.sub_model = formData.vehicle_sub_model;
    if (formData.vehicle_body_type) vehicleInfo.body_type = formData.vehicle_body_type;
    if (formData.vehicle_fuel_type) vehicleInfo.fuel_type = formData.vehicle_fuel_type;
    if (formData.vehicle_lookup_source) vehicleInfo.lookup_source = formData.vehicle_lookup_source;
    if (formData.tyre_specs.width) vehicleInfo.tyre_specs = formData.tyre_specs;

    const payload = {
      request_type: formData.request_type,
      rego: formData.rego || null,
      tyre_specs: formData.tyre_specs.width ? formData.tyre_specs : null,
      brand_preference: formData.brand_preference || null,
      price_range: formData.price_range,
      // Feb 2026: comparison intent
      comparison_intent: hasTyresSelected ? (formData.comparison_intent || "open") : null,
      tyre_brand: hasTyresSelected ? (formData.tyre_brand || null) : null,
      tyre_model: hasTyresSelected ? (formData.tyre_model || null) : null,
      driving_priority: hasTyresSelected && formData.comparison_intent === "budget"
        ? (formData.driving_priority || null)
        : null,
      service_type: getServiceType(),
      selected_services: formData.selected_services,
      search_radius: formData.search_radius,
      address: formData.address,
      quantity: hasTyresSelected ? formData.quantity : null,
      customer_note: formData.customer_note || null,
      vehicle_info: vehicleInfo
    };

    try {
      if (formData.request_type === "urgent") {
        // Urgent request — $5 priority fee applies
        const res = await axios.post(
          `${API}/payments/create-urgent-checkout`,
          {
            origin_url: window.location.origin,
            form_data: payload
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Urgent quote request submitted successfully!");
        navigate("/customer/dashboard");
        return;
      }

      // Standard request - submit directly
      await axios.post(
        `${API}/customer/quote-request`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Quote request submitted successfully!");
      navigate("/customer/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.selected_services.length > 0;
      case 2:
        // "What are you looking for?" intent. Only reached when tyres selected.
        if (!formData.comparison_intent) return false;
        if (formData.comparison_intent === "specific") {
          if (!formData.tyre_brand.trim() || !formData.tyre_model.trim()) return false;
        }
        if (formData.comparison_intent === "budget") {
          if (!formData.driving_priority) return false;
        }
        return true;
      case 3:
        // Need address always
        if (!formData.address?.full_address) return false;
        // Rego validation
        if (regoError) return false;
        // Rego is mandatory when tyres are selected or for non-tyres-only services
        if ((hasTyresSelected || isRegoRequired) && !formData.rego) return false;
        // When a rego is on file, the customer MUST either complete a CarJam
        // lookup OR explicitly choose to fall back to manual entry. This
        // prevents customers from racing past the (orange) Look up button
        // without giving shops the vehicle context they need to quote.
        if (formData.rego && !regoError) {
          const lookupResolved =
            lookupState.status === "found" || lookupState.status === "manual";
          if (!lookupResolved) return false;
        }
        // For tyre services: tyre specs (width + profile + rim_size) are mandatory
        if (hasTyresSelected && !(formData.tyre_specs.width && formData.tyre_specs.aspect_ratio && formData.tyre_specs.rim_size)) return false;
        return true;
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO page="quoteRequest" />
      
      {/* Header */}
      <header className="p-4 flex items-center justify-between border-b border-border">
        <Link to="/customer/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Dashboard</span>
        </Link>
        <Link to="/">
          <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-8" />
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Progress Bar — denominator collapses to 4 when no tyres (step 2 skipped). */}
      <div className="w-full bg-muted h-1">
        <div 
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        ></div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-2">
            {hasTyresSelected
              ? `Step ${step} of ${totalSteps}`
              : `Step ${step > 2 ? step - 1 : step} of ${totalSteps - 1}`}
          </p>
          <h1 className="font-heading text-3xl font-bold">
            {step === 1 && "What service do you need?"}
            {step === 2 && "What are you looking for?"}
            {step === 3 && "Tell us about your vehicle"}
            {step === 4 && "Set your preferences"}
            {step === 5 && "Review & Submit"}
          </h1>
        </div>

        {/* Step 1: Service Selection - Multi-select */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-1-content">
            <p className="text-muted-foreground">
              Select one or more services you need. You can combine services in a single request.
            </p>
            
            <div className="space-y-3">
              {SERVICES.map((service) => (
                <div
                  key={service.id}
                  className={`flex items-center space-x-4 p-4 rounded-lg border transition-all cursor-pointer ${
                    formData.selected_services.includes(service.id)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  onClick={() => toggleService(service.id)}
                  data-testid={`service-${service.id}`}
                >
                  <Checkbox 
                    checked={formData.selected_services.includes(service.id)}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => toggleService(service.id)}
                    id={service.id}
                  />
                  <div className="flex-1">
                    <Label htmlFor={service.id} className="text-base font-medium cursor-pointer">
                      {service.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  </div>
                  {formData.selected_services.includes(service.id) && (
                    <CheckCircle className="h-5 w-5 text-primary" />
                  )}
                </div>
              ))}
            </div>

            {formData.selected_services.length > 0 && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <p className="text-sm font-medium">Selected services:</p>
                  <p className="text-sm text-muted-foreground">
                    {formData.selected_services.map(s => 
                      SERVICES.find(srv => srv.id === s)?.label
                    ).join(", ")}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: What are you looking for? (intent) — only when tyres selected */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2-content">
            <p className="text-sm text-muted-foreground">
              This helps tyre shops give you more accurate, comparable quotes.
            </p>

            <RadioGroup
              value={formData.comparison_intent}
              onValueChange={(value) => setFormData({ ...formData, comparison_intent: value })}
              className="space-y-3"
            >
              {COMPARISON_INTENTS.map((intent) => (
                <label
                  key={intent.value}
                  htmlFor={`intent-${intent.value}`}
                  className={`block cursor-pointer rounded-lg border p-4 transition-colors ${
                    formData.comparison_intent === intent.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`intent-option-${intent.value}`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value={intent.value} id={`intent-${intent.value}`} className="mt-1" />
                    <div className="flex-1">
                      <div className="font-medium">{intent.label}</div>
                      <p className="text-sm text-muted-foreground mt-0.5">{intent.description}</p>
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>

            {/* Intent-specific follow-up fields */}
            {formData.comparison_intent === "specific" && (
              <div className="space-y-4 p-4 rounded-lg border border-primary/30 bg-primary/5" data-testid="intent-specific-fields">
                <div className="space-y-2">
                  <Label htmlFor="tyre_brand">Tyre Brand *</Label>
                  <Input
                    id="tyre_brand"
                    placeholder="e.g. Michelin, Bridgestone, Goodyear"
                    value={formData.tyre_brand}
                    onChange={(e) => setFormData({ ...formData, tyre_brand: e.target.value })}
                    data-testid="tyre-brand-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tyre_model">Tyre Model *</Label>
                  <Input
                    id="tyre_model"
                    placeholder="e.g. Pilot Sport 5, Turanza T005, Assurance"
                    value={formData.tyre_model}
                    onChange={(e) => setFormData({ ...formData, tyre_model: e.target.value })}
                    data-testid="tyre-model-input"
                  />
                </div>
                <div className="text-xs text-muted-foreground flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  Shops will quote this exact tyre where available, and may offer alternatives separately.
                </div>
              </div>
            )}

            {formData.comparison_intent === "budget" && (
              <div className="space-y-4 p-4 rounded-lg border border-primary/30 bg-primary/5" data-testid="intent-budget-fields">
                <div className="space-y-2">
                  <Label>Budget Level *</Label>
                  <RadioGroup
                    value={formData.price_range}
                    onValueChange={(value) => setFormData({ ...formData, price_range: value })}
                    className="grid grid-cols-3 gap-2"
                  >
                    {BUDGET_LEVELS.map((b) => (
                      <label
                        key={b.value}
                        htmlFor={`budget-${b.value}`}
                        className={`cursor-pointer rounded-md border px-3 py-2 text-center text-sm transition-colors ${
                          formData.price_range === b.value
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:border-primary/50"
                        }`}
                        data-testid={`budget-${b.value}`}
                      >
                        <RadioGroupItem value={b.value} id={`budget-${b.value}`} className="sr-only" />
                        {b.label}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>Driving Priority *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {DRIVING_PRIORITIES.map((p) => (
                      <button
                        type="button"
                        key={p.value}
                        onClick={() => setFormData({ ...formData, driving_priority: p.value })}
                        className={`rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                          formData.driving_priority === p.value
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:border-primary/50"
                        }`}
                        data-testid={`priority-${p.value}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="tyre_brand_opt">Preferred Brand <span className="text-muted-foreground">(optional)</span></Label>
                    <Input
                      id="tyre_brand_opt"
                      placeholder="e.g. Michelin"
                      value={formData.tyre_brand}
                      onChange={(e) => setFormData({ ...formData, tyre_brand: e.target.value })}
                      data-testid="tyre-brand-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tyre_model_opt">Preferred Model <span className="text-muted-foreground">(optional)</span></Label>
                    <Input
                      id="tyre_model_opt"
                      placeholder="e.g. Primacy 4"
                      value={formData.tyre_model}
                      onChange={(e) => setFormData({ ...formData, tyre_model: e.target.value })}
                      data-testid="tyre-model-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.comparison_intent === "open" && (
              <div className="p-4 rounded-lg border border-amber-500/40 bg-amber-500/5" data-testid="intent-open-warning">
                <div className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-700 dark:text-amber-400">Heads up</p>
                    <p className="text-muted-foreground mt-0.5">
                      Quotes may vary significantly in tyre brand, performance, durability and price.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="text-xs text-muted-foreground flex items-start gap-1.5 mt-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary" />
              <span>
                <strong>Tip:</strong> Choosing a preferred tyre brand/model helps you compare quotes more accurately — otherwise you may receive quotes for completely different tyre types and price ranges.
              </span>
            </div>
          </div>
        )}

        {/* Step 3: Vehicle Details */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-3-content">
            {/* Address - always first */}
            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Your Address *
              </Label>
              <AddressAutocomplete
                value={formData.address}
                onChange={(address) => updateFormData("address", address)}
                placeholder="Start typing your address..."
                required
              />
              <p className="text-xs text-muted-foreground">
                Used to calculate distance to nearby shops
              </p>
            </div>

            {/* Vehicle Info Section - Grouped */}
            <Card className="border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Car className="h-5 w-5 text-primary" />
                  Vehicle Info
                </CardTitle>
                <CardDescription>
                  {hasOnlyNonTyreServices 
                    ? "Vehicle registration is required for alignment and rotation services"
                    : isRegoRequired
                      ? "Enter your vehicle rego and the tyre specifications"
                      : "Enter your vehicle rego and the tyre specifications"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rego" className="flex items-center gap-1">
                    Vehicle Registration (Rego)
                    {(hasTyresSelected || isRegoRequired) && <span className="text-destructive">*</span>}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="rego"
                      type="text"
                      placeholder="e.g. ABC123"
                      value={formData.rego}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().slice(0, 6);
                        validateRego(val);
                        updateFormData("rego", val);
                        // Invalidate any previous lookup result if rego changes.
                        if (lookupState.status !== "idle") {
                          setLookupState({ status: "idle", message: "" });
                        }
                      }}
                      maxLength={6}
                      className={`uppercase ${regoError ? "border-destructive" : ""}`}
                      required={hasTyresSelected || isRegoRequired}
                      data-testid="rego-input"
                    />
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!formData.rego || regoError) {
                          toast.error("Enter a valid rego first");
                          return;
                        }
                        setLookupState({ status: "loading", message: "" });
                        try {
                          const res = await axios.get(`${API}/customer/vehicle-lookup`, {
                            params: { plate: formData.rego },
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (res.data.status === "found") {
                            const v = res.data.vehicle || {};
                            updateFormData("vehicle_make", v.make || "");
                            updateFormData("vehicle_model", v.model || "");
                            updateFormData("vehicle_year", v.year || "");
                            updateFormData("vehicle_sub_model", v.sub_model || "");
                            updateFormData("vehicle_body_type", v.body_type || "");
                            updateFormData("vehicle_fuel_type", v.fuel_type || "");
                            updateFormData("vehicle_lookup_source", "carjam");
                            setLookupState({ status: "found", message: "" });
                            toast.success("Vehicle details found");
                          } else {
                            setLookupState({
                              status: res.data.status || "error",
                              message: res.data.message || "Lookup failed",
                            });
                          }
                        } catch (err) {
                          setLookupState({
                            status: "error",
                            message: err?.response?.data?.detail || "Lookup failed",
                          });
                        }
                      }}
                      disabled={!formData.rego || !!regoError || lookupState.status === "loading"}
                      data-testid="vehicle-lookup-btn"
                      className={`shrink-0 font-semibold gap-1.5 ${
                        lookupState.status === "found"
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-primary hover:bg-primary/90 text-primary-foreground"
                      } ${
                        formData.rego &&
                        !regoError &&
                        !["loading", "found", "manual"].includes(lookupState.status)
                          ? "lookup-pulse-attention"
                          : ""
                      }`}
                    >
                      {lookupState.status === "loading" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Looking up…
                        </>
                      ) : lookupState.status === "found" ? (
                        <>
                          <Check className="h-4 w-4" />
                          Vehicle found
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Look up vehicle
                        </>
                      )}
                    </Button>
                  </div>
                  {regoError && (
                    <p className="text-xs text-destructive flex items-center gap-1" data-testid="rego-error">
                      <AlertCircle className="h-3 w-3" />
                      {regoError}
                    </p>
                  )}
                  {/* Lookup status messaging */}
                  {lookupState.status === "found" && (
                    <p className="text-xs text-green-600 flex items-center gap-1" data-testid="lookup-found-msg">
                      <Check className="h-3 w-3" />
                      We found your vehicle. Review the details below and edit if anything is wrong.
                    </p>
                  )}
                  {(lookupState.status === "not_found" || lookupState.status === "pending" || lookupState.status === "error") && (
                    <div className="text-xs flex items-start gap-1 text-amber-600" data-testid="lookup-fallback-msg">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>
                        {lookupState.message || "We couldn't find your vehicle."}{" "}
                        <button
                          type="button"
                          className="underline font-medium"
                          onClick={() => {
                            setLookupState({ status: "manual", message: "" });
                            updateFormData("vehicle_lookup_source", "manual");
                          }}
                          data-testid="enter-vehicle-manually-btn"
                        >
                          Enter details manually
                        </button>
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {(hasTyresSelected || isRegoRequired)
                      ? "Required so shops can identify your vehicle make and model"
                      : "Optional - helps shops look up your vehicle specs"}
                  </p>
                  {/* Hard nudge: blocks the customer from racing past
                      the rego step without resolving the lookup. The
                      orange pulsing button + this line are the two
                      explicit signals. */}
                  {formData.rego &&
                    !regoError &&
                    !["found", "manual", "loading"].includes(lookupState.status) && (
                      <p
                        className="text-xs text-orange-600 dark:text-orange-300 font-medium flex items-center gap-1"
                        data-testid="lookup-required-hint"
                      >
                        <AlertCircle className="h-3 w-3" />
                        Tap <strong>Look up vehicle</strong> to fetch your
                        make/model — this helps shops quote accurately.
                      </p>
                    )}
                </div>

                {/* Vehicle details (shown after a successful lookup OR manual entry). */}
                {(lookupState.status === "found" || lookupState.status === "manual") && (
                  <div
                    className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3"
                    data-testid="vehicle-details-section"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-medium">
                        {lookupState.status === "found" ? "Verify your vehicle details" : "Enter your vehicle details"}
                      </h4>
                      <span className="text-[10px] text-muted-foreground">
                        {lookupState.status === "found"
                          ? "Source: CarJam · editable"
                          : "Manually entered"}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="v_make" className="text-xs">Make *</Label>
                        <Input
                          id="v_make"
                          placeholder="e.g. Toyota, Mercedes-Benz"
                          value={formData.vehicle_make}
                          onChange={(e) => updateFormData("vehicle_make", e.target.value)}
                          data-testid="vehicle-make-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="v_model" className="text-xs">Model *</Label>
                        <Input
                          id="v_model"
                          placeholder="e.g. Corolla, GLE 300d"
                          value={formData.vehicle_model}
                          onChange={(e) => updateFormData("vehicle_model", e.target.value)}
                          data-testid="vehicle-model-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="v_year" className="text-xs">Year</Label>
                        <Input
                          id="v_year"
                          type="number"
                          inputMode="numeric"
                          placeholder="e.g. 2020"
                          value={formData.vehicle_year}
                          onChange={(e) => updateFormData("vehicle_year", e.target.value)}
                          data-testid="vehicle-year-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="v_submodel" className="text-xs">Sub-model / Variant</Label>
                        <Input
                          id="v_submodel"
                          placeholder="e.g. GLC 300d 4MATIC"
                          value={formData.vehicle_sub_model}
                          onChange={(e) => updateFormData("vehicle_sub_model", e.target.value)}
                          data-testid="vehicle-submodel-input"
                        />
                      </div>
                      {formData.vehicle_body_type && (
                        <div className="space-y-1">
                          <Label htmlFor="v_body" className="text-xs">Body Type</Label>
                          <Input
                            id="v_body"
                            value={formData.vehicle_body_type}
                            onChange={(e) => updateFormData("vehicle_body_type", e.target.value)}
                            data-testid="vehicle-body-input"
                          />
                        </div>
                      )}
                      {formData.vehicle_fuel_type && (
                        <div className="space-y-1">
                          <Label htmlFor="v_fuel" className="text-xs">Fuel Type</Label>
                          <Input
                            id="v_fuel"
                            value={formData.vehicle_fuel_type}
                            onChange={(e) => updateFormData("vehicle_fuel_type", e.target.value)}
                            data-testid="vehicle-fuel-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Only show tyre specs section for tyre services */}
                {hasTyresSelected && (
                  <>
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Tyre Specifications</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Tyre Size <span className="text-destructive">*</span></Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="width" className="text-xs text-muted-foreground">Width</Label>
                          <Input
                            id="width"
                            type="text"
                            inputMode="numeric"
                            placeholder="205"
                            value={formData.tyre_specs.width}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                              updateTyreSpecs("width", val);
                              if (tyreErrors.width) setTyreErrors(prev => ({ ...prev, width: "" }));
                            }}
                            className={tyreErrors.width ? "border-destructive" : ""}
                            data-testid="tyre-width-input"
                          />
                          {tyreErrors.width && <p className="text-xs text-destructive">{tyreErrors.width}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="aspect" className="text-xs text-muted-foreground">Aspect Ratio</Label>
                          <Input
                            id="aspect"
                            type="text"
                            inputMode="numeric"
                            placeholder="55"
                            value={formData.tyre_specs.aspect_ratio}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                              updateTyreSpecs("aspect_ratio", val);
                              if (tyreErrors.aspect) setTyreErrors(prev => ({ ...prev, aspect: "" }));
                            }}
                            className={tyreErrors.aspect ? "border-destructive" : ""}
                            data-testid="tyre-aspect-input"
                          />
                          {tyreErrors.aspect && <p className="text-xs text-destructive">{tyreErrors.aspect}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rim" className="text-xs text-muted-foreground">Rim Size</Label>
                          <Input
                            id="rim"
                            type="text"
                            inputMode="numeric"
                            placeholder="16"
                            value={formData.tyre_specs.rim_size}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                              updateTyreSpecs("rim_size", val);
                              if (tyreErrors.rim) setTyreErrors(prev => ({ ...prev, rim: "" }));
                            }}
                            className={tyreErrors.rim ? "border-destructive" : ""}
                            data-testid="tyre-rim-input"
                          />
                          {tyreErrors.rim && <p className="text-xs text-destructive">{tyreErrors.rim}</p>}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Show quantity selector only for tyre services */}
            {hasTyresSelected && (
              <div className="space-y-2">
                <Label>How many tyres?</Label>
                <Select
                  value={formData.quantity.toString()}
                  onValueChange={(value) => updateFormData("quantity", parseInt(value))}
                >
                  <SelectTrigger data-testid="quantity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYRE_QUANTITIES.map((q) => (
                      <SelectItem key={q.value} value={q.value.toString()}>
                        {q.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Info for non-tyre services */}
            {hasOnlyNonTyreServices && (
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      For wheel alignment and rotation services, your vehicle registration is required 
                      so shops can identify your vehicle make and model before quoting.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 4: Preferences */}
        {step === 4 && (
          <div className="space-y-8" data-testid="step-4-content">
            {/* Price Range — only shown for tyre services AND when intent didn't already collect it.
                "specific" intent already locked-in brand/model; "budget" intent already collected budget level.
                Only "open" still needs to pick a price range here. */}
            {hasTyresSelected && formData.comparison_intent !== "specific" && formData.comparison_intent !== "budget" && (
              <div className="space-y-4">
                <Label>Price Range Preference</Label>
                <RadioGroup
                  value={formData.price_range}
                  onValueChange={(value) => updateFormData("price_range", value)}
                  className="grid grid-cols-2 gap-3"
                >
                  {PRICE_RANGES.map((range) => (
                    <div
                      key={range.value}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                        formData.price_range === range.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => updateFormData("price_range", range.value)}
                      data-testid={`price-${range.value}`}
                    >
                      <RadioGroupItem value={range.value} id={range.value} />
                      <div>
                        <Label htmlFor={range.value} className="text-sm font-medium cursor-pointer">
                          {range.label}
                        </Label>
                        <p className="text-xs text-muted-foreground">{range.description}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Search Radius */}
            <div className="space-y-4">
              <Label className="flex items-center justify-between">
                <span>Search Radius</span>
                <span className="text-primary font-semibold">{formData.search_radius} km</span>
              </Label>
              
              <div className="relative pt-2 pb-8">
                <Slider
                  value={[formData.search_radius]}
                  onValueChange={([value]) => updateFormData("search_radius", value)}
                  min={5}
                  max={30}
                  step={5}
                  className="w-full"
                  data-testid="radius-slider"
                />
                
                <div className="absolute left-0 right-0 bottom-0 flex justify-between text-xs text-muted-foreground">
                  <span>5 km</span>
                  <span>30 km</span>
                </div>
              </div>

              {/* Radius explanation */}
              <Card className={`transition-all ${
                formData.search_radius < 20 ? "border-yellow-500/50 bg-yellow-500/5" :
                "border-border"
              }`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <Info className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    formData.search_radius < 20 ? "text-yellow-500" :
                    "text-muted-foreground"
                  }`} />
                  <div className="text-sm">
                    {formData.search_radius < 20 && (
                      <p>A smaller radius may limit your options. Consider increasing to get more competitive quotes.</p>
                    )}
                    {formData.search_radius >= 20 && (
                      <p>A {formData.search_radius} km radius is a good balance between convenience and competition.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Brand Preference — legacy free-text field, only shown when intent="open"
                (specific/budget intents already collected tyre_brand/tyre_model in step 2). */}
            {hasTyresSelected && formData.comparison_intent === "open" && (
              <div className="space-y-2">
                <Label htmlFor="brand">Preferred Brand/Model (Optional)</Label>
                <Input
                  id="brand"
                  type="text"
                  placeholder="e.g. Michelin Pilot Sport, Bridgestone Potenza..."
                  value={formData.brand_preference}
                  onChange={(e) => updateFormData("brand_preference", e.target.value)}
                  data-testid="brand-input"
                />
                <p
                  className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"
                  data-testid="brand-helper-note"
                >
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-yellow-500" />
                  <span>
                    Pick a brand/model so shops quote comparable tyres. Leave it blank
                    and you may get very different tyre types — making quotes hard to
                    compare on anything but price.
                  </span>
                </p>
              </div>
            )}

            {/* Customer Note - Required */}
            <div className="space-y-2">
              <Label htmlFor="customer_note">Note to Tyre Shops <span className="text-destructive">*</span></Label>
              <Textarea
                id="customer_note"
                placeholder="e.g. I'm looking for a durable all-season tyre for daily commuting. Open to recommendations on brands that offer the best value."
                value={formData.customer_note}
                onChange={(e) => updateFormData("customer_note", e.target.value)}
                className={`min-h-[80px] ${!formData.customer_note?.trim() && formData.customer_note !== "" ? "border-destructive" : ""}`}
                data-testid="customer-note-input"
              />
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Help shops give you the best quote by sharing a little detail:</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>Ask for recommendations on the best tyre brands for your vehicle</li>
                  <li>Mention anything the shop should consider when preparing your quote</li>
                </ul>
              </div>
            </div>

            {/* Urgency */}
            <Card className="border-primary/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.request_type === "urgent" ? "bg-primary text-primary-foreground" : "bg-muted"
                    }`}>
                      <Zap className="h-5 w-5" />
                    </div>
                    <div>
                      <Label htmlFor="urgent" className="text-base font-medium cursor-pointer">
                        Urgent Request
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Get quotes faster (+$5.00 fee)
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="urgent"
                    checked={formData.request_type === "urgent"}
                    onCheckedChange={(checked) => updateFormData("request_type", checked ? "urgent" : "standard")}
                    data-testid="urgent-switch"
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {formData.request_type === "urgent" 
                        ? "Requests close in 3 business days" 
                        : "Requests close in 5 business days"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-5-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Request Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Services</p>
                    <p className="font-medium">
                      {formData.selected_services.map(s => 
                        SERVICES.find(srv => srv.id === s)?.label
                      ).join(", ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Urgency</p>
                    <p className="font-medium flex items-center gap-2">
                      {formData.request_type === "urgent" ? (
                        <>
                          <Zap className="h-4 w-4 text-primary" />
                          Urgent (+$5.00)
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4" />
                          Standard
                        </>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{formData.address?.full_address || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Search Radius</p>
                    <p className="font-medium">{formData.search_radius} km</p>
                  </div>
                  {(hasTyresSelected || formData.rego) && (
                    <div>
                      <p className="text-sm text-muted-foreground">Vehicle Rego</p>
                      <p className="font-medium">{formData.rego || "Not provided"}</p>
                    </div>
                  )}
                  {(formData.vehicle_make || formData.vehicle_model || formData.vehicle_year) && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Vehicle</p>
                      <p className="font-medium" data-testid="review-vehicle-line">
                        {[formData.vehicle_year, formData.vehicle_make, formData.vehicle_model].filter(Boolean).join(" ")}
                        {formData.vehicle_sub_model && (
                          <span className="text-muted-foreground"> · {formData.vehicle_sub_model}</span>
                        )}
                      </p>
                      {(formData.vehicle_body_type || formData.vehicle_fuel_type || formData.vehicle_lookup_source) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[formData.vehicle_body_type, formData.vehicle_fuel_type].filter(Boolean).join(" · ")}
                          {formData.vehicle_lookup_source && (
                            <span className="ml-2 inline-flex items-center gap-1">
                              {formData.vehicle_lookup_source === "carjam" ? (
                                <>
                                  <Check className="h-3 w-3 text-green-600" />
                                  <span className="text-green-600">CarJam verified</span>
                                </>
                              ) : (
                                <span>Manually entered</span>
                              )}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                  {hasTyresSelected && formData.comparison_intent && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Looking For</p>
                      <p className="font-medium">
                        {COMPARISON_INTENTS.find(i => i.value === formData.comparison_intent)?.label}
                      </p>
                      {formData.comparison_intent === "specific" && (formData.tyre_brand || formData.tyre_model) && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Tyre: </span>
                          <span className="font-medium">{formData.tyre_brand} {formData.tyre_model}</span>
                        </p>
                      )}
                      {formData.comparison_intent === "budget" && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Budget: </span>
                          <span className="font-medium">{BUDGET_LEVELS.find(b => b.value === formData.price_range)?.label}</span>
                          {formData.driving_priority && (
                            <>
                              <span className="text-muted-foreground"> · Priority: </span>
                              <span className="font-medium">{DRIVING_PRIORITIES.find(p => p.value === formData.driving_priority)?.label}</span>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                  {hasTyresSelected && (
                    <div>
                      <p className="text-sm text-muted-foreground">Tyre Size</p>
                      <p className="font-medium">
                        {formData.tyre_specs.width ? `${formData.tyre_specs.width}/${formData.tyre_specs.aspect_ratio || "—"}R${formData.tyre_specs.rim_size || "—"}` : "Not provided"}
                      </p>
                    </div>
                  )}
                  {hasTyresSelected && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Quantity</p>
                        <p className="font-medium">{formData.quantity} tyres</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Price Range</p>
                        <p className="font-medium">
                          {PRICE_RANGES.find(p => p.value === formData.price_range)?.label || BUDGET_LEVELS.find(b => b.value === formData.price_range)?.label}
                        </p>
                      </div>
                    </>
                  )}
                  {formData.brand_preference && hasTyresSelected && formData.comparison_intent === "open" && (
                    <div>
                      <p className="text-sm text-muted-foreground">Preferred Brand/Model</p>
                      <p className="font-medium">{formData.brand_preference}</p>
                    </div>
                  )}
                  {formData.customer_note && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Note to Shops</p>
                      <p className="font-medium">{formData.customer_note}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {formData.request_type === "urgent" && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <Zap className="h-5 w-5 text-primary mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-primary">Urgent Request — $5.00 NZD Fee</p>
                    <p className="text-muted-foreground mt-1">
                      A $5.00 priority processing fee applies to urgent requests. Your request will be fast-tracked to nearby shops immediately after submission.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-green-500/30 bg-green-500/5">
              <CardContent className="p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-600 dark:text-green-400">What happens next?</p>
                  <p className="text-muted-foreground mt-1">
                    Your request will be sent to tyre shops within {formData.search_radius} km of your location. 
                    They'll submit quotes and you'll be notified by email. Your details remain private until you choose a quote.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-border">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => {
                // Skip step 2 (intent) on the way back if no tyres selected
                if (step === 3 && !hasTyresSelected) {
                  setStep(1);
                } else {
                  setStep(step - 1);
                }
              }}
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          ) : (
            <div></div>
          )}

          {step < totalSteps ? (
            <Button
              onClick={() => {
                // Step 1 → Step 2/3: skip the intent step entirely if no tyres
                if (step === 1 && !hasTyresSelected) {
                  setStep(3);
                  return;
                }
                // Validate tyre specs on step 3 (Vehicle Details) before proceeding
                if (step === 3 && hasTyresSelected) {
                  const errors = { width: "", aspect: "", rim: "" };
                  let hasError = false;
                  const w = parseInt(formData.tyre_specs.width);
                  const a = parseInt(formData.tyre_specs.aspect_ratio);
                  const r = parseInt(formData.tyre_specs.rim_size);
                  if (formData.tyre_specs.width && (isNaN(w) || w < 150 || w > 320)) {
                    errors.width = "Width is outside the accepted range";
                    hasError = true;
                  }
                  if (formData.tyre_specs.aspect_ratio && (isNaN(a) || a < 10 || a > 100)) {
                    errors.aspect = "Aspect ratio is outside the accepted range";
                    hasError = true;
                  }
                  if (formData.tyre_specs.rim_size && (isNaN(r) || r < 13 || r > 25)) {
                    errors.rim = "Rim size is outside the accepted range";
                    hasError = true;
                  }
                  setTyreErrors(errors);
                  if (hasError) return;
                }
                setStep(step + 1);
              }}
              disabled={!canProceed()}
              className="bg-primary text-primary-foreground"
              data-testid="next-step-btn"
              title={
                step === 3 &&
                formData.rego &&
                !regoError &&
                !["found", "manual"].includes(lookupState.status)
                  ? "Please look up your vehicle (or choose to enter details manually) first"
                  : ""
              }
            >
              Continue
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-primary text-primary-foreground"
              data-testid="submit-request-btn"
            >
              {loading ? "Processing..." : formData.request_type === "urgent" ? "Pay $5 & Submit" : "Submit Request"}
              <CheckCircle className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
